import {ReactFlow, SelectionMode} from '@xyflow/react';
import {FrameNode} from '../[id]/FrameNode';
import {useCanvas} from './useCanvas';
import {useContext, useEffect, useCallback, useRef, useState} from 'react';
import {ReadonlyContext} from '@features/contexts/ReadonlyContext';
import {ZoomCSSVariableSync} from './ZoomCSSVariableSync';
import {ActiveScreenLocalStorageSync} from '../localStorage/lastSelectedScreen';
import {useAtom, useAtomValue} from 'jotai';
import {interactionModeAtom} from './InteractionMode';
import {useSetActiveScreenId} from '@features/activeScreenIdContext/useSetActiveScreenId';
import {useGetActiveScreenId} from '@features/activeScreenIdContext/useGetActiveScreenId';
import {useToggleScreenSelected} from '@features/activeScreenIdContext/useToggleScreenSelected';
import {useGetSelectedScreenIds} from '@features/activeScreenIdContext/useGetSelectedScreenIds';
import {modeAtom, useIsEditingTextRef} from '@features/frame/partialEdit/atoms';
import {cleanupPartialEditSelection} from '@features/frame/partialEdit/usePartialEdit';
import {useFrameSelectionActions} from '@features/frame/partialEdit/useFrameSelection';
import {cn} from '@features/ui/utils';
import amplitudeService from '@features/analytics/amplitudeService';
import {FlowContext} from '@features/contexts/FlowContext';
import {useChangeFlowVersion, useDeleteScreen} from '../flowHook';
import {DeletionModal} from './DeletionModal';
import {isEditableElementFocused} from '@features/utils/dom';
import {isComponentLibraryItem} from '@features/flow/utils/componentScreenId';
import {captureException} from '@sentry/react';
import {ClipboardKeyboardHandler} from './ClipboardKeyboardHandler';
import {WorkspaceSync} from './WorkspaceSync';
import {trpc} from '@features/utils/trpc';
import {useAiThinking} from '@features/ai/useAiThinking';
import {useIsFlowBusy} from '@/app/flow/hooks/useIsFlowBusy';
import {ExportButtonDisabledBridge} from './useExportToFigma';

const nodeTypes = {
  frame: FrameNode,
  component: FrameNode,
};

export {type FrameNodeType} from './useCanvas';

export const Canvas = () => {
  const {
    nodes,
    onNodesChange,
    onNodeDragStart,
    onNodeDragStop,
    onMoveEnd,
    isDragging,
  } = useCanvas();

  const {readonly} = useContext(ReadonlyContext);
  const nodesDraggable = !readonly;
  const [interactionMode, setInteractionMode] = useAtom(interactionModeAtom);
  const setActiveScreenId = useSetActiveScreenId();
  const toggleScreenSelected = useToggleScreenSelected();
  const getActiveScreenId = useGetActiveScreenId();
  // Imperative read for the keydown handler so we don't re-attach the listener
  // on every selection change.
  const getSelectedScreenIds = useGetSelectedScreenIds();
  const mode = useAtomValue(modeAtom);
  const {flow} = useContext(FlowContext);
  const deleteScreen = useDeleteScreen(flow.id);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [screenIdsToDelete, setScreenIdsToDelete] = useState<string[]>([]);
  const {clearFrameSelection, getFrameSelection} = useFrameSelectionActions();

  // Keep latest `nodes` in a ref so the partial-edit keydown listener can read
  // it for Tab cycling without re-attaching on every node update (drag, sync).
  const nodesRef = useRef(nodes);
  // eslint-disable-next-line react-hooks/refs -- intentional stable ref for keydown handler
  nodesRef.current = nodes;

  // Screen-level keyboard shortcuts (active only while partial-edit mode is on
  // AND there is no inner element selection — inner navigation is handled by
  // usePartialEdit's own keydown listener):
  //   Escape  — deselect screen
  //   Enter   — select the first banani-button inside the active screen
  //   Tab/Sh+ — cycle through screens
  // Capture phase ensures we run before React Flow's internal Tab handling.
  useEffect(() => {
    if (mode !== 'partial-edit') return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableElementFocused()) return;
      const activeId = getActiveScreenId();
      if (!activeId) return;

      // Screen-level shortcuts only apply when nothing inside the active frame
      // is selected. When an inner element is selected, usePartialEdit owns
      // Escape/Enter/Tab so Esc first clears the element selection and keeps
      // the screen active.
      if (getFrameSelection().kind !== 'none') return;

      if (event.key === 'Escape') {
        event.preventDefault();
        setTimeout(() => setActiveScreenId(null), 0);
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        // Structure: the outer `[data-banani-screenid]` is the shadow host;
        // inside its shadowRoot, the content div carries the same attribute.
        // We dig one level deep to reach the button inside the frame content.
        const host = document.querySelector(
          `[data-banani-screenid="${activeId}"]`,
        );
        const contentDiv = host?.shadowRoot?.querySelector(
          '[data-banani-screenid]',
        );
        const child = contentDiv?.querySelector(
          '[data-media-type="banani-button"]',
        );
        if (!(child instanceof HTMLElement)) return;
        event.preventDefault();
        // Dispatch a real click so usePartialEdit.handleClick handles the
        // full selection flow. Defer to the next frame so the target's
        // layout/shadow DOM is fully settled after a screen-switch.
        requestAnimationFrame(() => child.click());
        return;
      }

      if (event.key === 'Tab') {
        const screenIds = nodesRef.current
          .filter((n) => n.type === 'frame' || n.type === 'component')
          .map((n) => n.id);
        if (screenIds.length < 2) return;
        const idx = screenIds.indexOf(activeId);
        if (idx === -1) return;
        const nextIdx = event.shiftKey
          ? (idx - 1 + screenIds.length) % screenIds.length
          : (idx + 1) % screenIds.length;
        event.preventDefault();
        setActiveScreenId(screenIds[nextIdx]);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [mode, getActiveScreenId, getFrameSelection, setActiveScreenId]);

  // Undo/Redo state for keyboard shortcuts
  const undo = trpc.flow.getUndoStatus.useQuery({flowId: flow.id}).data;
  const {mutate: changeFlowVersion} = useChangeFlowVersion();
  const isChangingFlowVersion = useIsFlowBusy();
  const isGenerating = useAiThinking() || isChangingFlowVersion;

  // Refs for stable event handler references (Fix #4)
  const modeRef = useRef(mode);
  const isEditingTextRef = useIsEditingTextRef();
  // eslint-disable-next-line react-hooks/refs -- intentional stable ref for keydown handler
  modeRef.current = mode;

  // Clears any in-frame selection. Mode (`partial-edit` ↔ `generate`) is
  // owned by useSelectModeShortcut and flips automatically based on active
  // screen + content, so we don't touch it here.
  const clearInnerSelection = useCallback(() => {
    cleanupPartialEditSelection();
    clearFrameSelection();
  }, [clearFrameSelection]);

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: {id: string}) => {
      if (event.target instanceof HTMLElement && event.target.shadowRoot)
        return;

      // Blur any focused editable element (e.g. the prompt textarea) so
      // keyboard shortcuts like Enter/Tab reach the canvas handlers.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      clearInnerSelection();

      // Cmd/Ctrl-click: toggle the clicked node in/out of the
      // multi-selection. If added, it becomes the new primary. If
      // removed, the next remaining node becomes primary (or clears).
      if (event.metaKey || event.ctrlKey) {
        toggleScreenSelected(node.id);
        return;
      }
      // Plain click (or shift-click — treated as plain): clicked node
      // becomes the sole selected screen.
      setActiveScreenId(node.id);
    },
    [setActiveScreenId, toggleScreenSelected, clearInnerSelection],
  );

  const onPaneClick = useCallback(() => {
    // Clear both active and multi-selection; URL active id is cleared too.
    setActiveScreenId(null);
    // In readonly the URL's active id is preserved; the keyboard handlers
    // and frame highlight follow the URL active. ReactFlow's pane click
    // already deselects nodes via onNodesChange, so we don't need to also
    // clear here.

    // if we're editing text, we need to give the text-edit code a chance to save.
    if (getFrameSelection().kind !== 'editing-text') {
      clearInnerSelection();
    }
  }, [setActiveScreenId, clearInnerSelection, getFrameSelection]);

  // Fix #2 & #3: Error handling + loading state
  const handleDeleteScreens = useCallback(async () => {
    setIsDeleting(true);
    try {
      try {
        await deleteScreen.mutateAsync({
          screens: screenIdsToDelete,
        });
      } catch (e) {
        captureException(e);
        return;
      }

      amplitudeService.logEvent('[Canvas] screens deleted', {
        count: screenIdsToDelete.length,
      });
      setActiveScreenId(null);
      setIsDeleteModalOpen(false);
      setScreenIdsToDelete([]);
    } catch (error) {
      captureException(error);
    } finally {
      setIsDeleting(false);
    }
  }, [screenIdsToDelete, deleteScreen, setActiveScreenId]);

  // Fix #4: Use refs to avoid re-attaching listeners on every selection change
  // Fix #5: Add blur handler to reset pan mode when window loses focus
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableElementFocused()) return;

      if (
        (event.key === 'h' || event.key === 'H') &&
        !isEditingTextRef.current
      ) {
        setInteractionMode('pan');
        amplitudeService.logEvent('[Canvas] interaction mode changed', {
          mode: 'pan',
          trigger: 'h-key',
        });
      } else if (event.key === ' ' && !isEditingTextRef.current) {
        event.preventDefault();
        setInteractionMode('pan');
      } else if (
        (event.key === 'v' || event.key === 'V') &&
        !event.metaKey &&
        !event.ctrlKey &&
        !isEditingTextRef.current
      ) {
        setInteractionMode('select');
        amplitudeService.logEvent('[Canvas] interaction mode changed', {
          mode: 'select',
          trigger: 'v-key',
        });
      } else if (
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        event.key.toLowerCase() === 'z' &&
        undo?.compatible &&
        !readonly &&
        !isGenerating
      ) {
        event.preventDefault();
        const version = event.shiftKey ? undo.redoVersion : undo.undoVersion;
        if (version) {
          changeFlowVersion({flowId: flow.id, version});
          amplitudeService.logEvent('[Canvas] undo/redo shortcut used', {
            action: event.shiftKey ? 'redo' : 'undo',
          });
        }
      } else if (
        (event.key === 'Delete' || event.key === 'Backspace') &&
        !readonly &&
        !isEditingTextRef.current &&
        getFrameSelection().kind === 'none'
      ) {
        const idsToDelete = getSelectedScreenIds();

        if (idsToDelete.length > 0) {
          setScreenIdsToDelete([...idsToDelete]);
          setIsDeleteModalOpen(true);
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (isEditableElementFocused()) return;

      if (event.key === ' ' && !isEditingTextRef.current) {
        setInteractionMode(readonly ? 'pan' : 'select');
      }
    };

    const handleWindowBlur = () => {
      setInteractionMode(readonly ? 'pan' : 'select');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [
    changeFlowVersion,
    flow.id,
    getSelectedScreenIds,
    getFrameSelection,
    isGenerating,
    readonly,
    setInteractionMode,
    undo,
    isEditingTextRef,
  ]);

  const isSelectMode = interactionMode === 'select';
  const isPanMode = interactionMode === 'pan';

  const hasComponentsInSelection = screenIdsToDelete.some(
    isComponentLibraryItem,
  );

  return (
    <>
      <DeletionModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleting}
        onClose={() => {
          if (isDeleting) return;
          setIsDeleteModalOpen(false);
          setScreenIdsToDelete([]);
        }}
        onDelete={handleDeleteScreens}
        deleteDisabled={hasComponentsInSelection}
        title={
          hasComponentsInSelection
            ? "Can't delete"
            : screenIdsToDelete.length === 1
              ? 'Delete screen'
              : `Delete ${screenIdsToDelete.length} screens`
        }
        body={
          hasComponentsInSelection
            ? "You can't delete components yet. Please select only screens to delete."
            : screenIdsToDelete.length === 1
              ? 'You are about to delete the selected screen. Are you sure?'
              : `You are about to delete ${screenIdsToDelete.length} selected screens. Are you sure?`
        }
      />
      <ReactFlow
        nodesDraggable={nodesDraggable && !isPanMode}
        elementsSelectable={!isPanMode}
        panOnScroll
        panOnDrag={isPanMode}
        selectionOnDrag={isSelectMode}
        selectionMode={SelectionMode.Partial}
        minZoom={0.01}
        onlyRenderVisibleElements={nodes.length > 30}
        proOptions={{hideAttribution: true}}
        nodeTypes={nodeTypes}
        nodes={nodes}
        onNodesChange={onNodesChange}
        onNodeClick={isPanMode ? undefined : onNodeClick}
        onPaneClick={onPaneClick}
        onNodeDragStart={isPanMode ? undefined : onNodeDragStart}
        onNodeDragStop={isPanMode ? undefined : onNodeDragStop}
        zoomOnDoubleClick={false}
        deleteKeyCode={null}
        onMoveEnd={onMoveEnd}
        multiSelectionKeyCode={['Meta', 'Control']}
        selectionKeyCode='Shift'
        className={cn(
          isDragging ? 'dragging-node' : '',
          isSelectMode && 'select-mode',
          isPanMode && 'pan-mode',
        )}
      >
        <ZoomCSSVariableSync />
        <ActiveScreenLocalStorageSync />
        <ClipboardKeyboardHandler />
        <WorkspaceSync />
        <ExportButtonDisabledBridge />
      </ReactFlow>
    </>
  );
};
